import { useEffect, useState } from 'react';
import {
  api, darf, datum, dokumentArt, zeitpunkt, type Anmelder,
} from '../api.ts';
import {
  Auswahl, Dialog, Fehlerkasten, Haken, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import { Dateikachel, type Kacheldatei } from '../teile/Dateikachel.tsx';
import { useDateifenster } from '../teile/Dateifenster.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { FREIGABE_VORGEMERKT, freigabeNehmen } from '../teile/sprungmarke.ts';
import { externSpaeter } from '../teile/extern.ts';
import {
  fristKlasse, fristText, laeuftNoch, type Fristlage,
} from '../teile/fristtext.ts';
import { fragen } from '../teile/rueckfrage.tsx';

// Die Behoerdenmappe eines Auftrags.
//
// Die eine Regel, die diesen Bildschirm formt: HERKUNFT SCHLAEGT INHALT.
// Unser Antrag auf eine verkehrsrechtliche Anordnung heisst fast wie der
// Bescheid und enthaelt dieselben Woerter. Nur der Bescheid traegt eine
// Frist. Früher sind daraus 158 falsche Haken entstanden. Deshalb gibt es
// hier ZWEI getrennte Wege: „erzeugen" (unser Papier) und „Eingang erfassen"
// (das Papier der Behoerde). Kein gemeinsames Formular mit einem Auswahlfeld.

const ERZEUGBAR = [
  { wert: 'AAZ', text: 'AAZ, Aufgrabungsanzeige' },
  { wert: 'VAO', text: 'VAO, Antrag auf verkehrsrechtliche Anordnung' },
  { wert: 'FAZ', text: 'FAZ, Fertigstellungsanzeige' },
  { wert: 'POLLER', text: 'Poller, Antrag auf Freigabe' },
];

const EINGANG = [
  { wert: 'VAO', text: 'VAO-Bescheid' },
  { wert: 'GEBUEHRENBESCHEID', text: 'Gebührenbescheid' },
  { wert: 'AUSNAHMEGENEHMIGUNG', text: 'Ausnahmegenehmigung' },
  { wert: 'POLLER', text: 'Pollerfreigabe' },
  { wert: 'SONSTIGES', text: 'Sonstiges Behördenpapier' },
];

/** Ein Betrag mit zwei Nachkommastellen und Komma. 87,5 ist kein Preis. */
function euro(w: unknown): string {
  const n = Number(w);
  return Number.isFinite(n)
    ? n.toLocaleString('de-DE', { minimumFractionDigits: 2,
                                  maximumFractionDigits: 2 })
    : '';
}

const ZUSTAND: Record<string, string> = {
  entwurf: 'Entwurf', erstellt: 'erstellt', im_ordner: 'liegt im Ordner',
  versendet: 'versendet', ueber_portal_eingestellt: 'über Portal eingestellt',
  ersetzt: 'ersetzt',
};

export default function Behoerdenmappe(
  { ich, auftragId }: { ich: Anmelder; auftragId: string }
) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [dialog, setDialog] = useState<null | 'erzeugen' | 'eingang'>(null);
  /* WELCHEM PAPIER GERADE EINE DATEI ZUGEORDNET WIRD.
     Marvin, 31.08.2026: „Ich muss die Möglichkeit haben, wenn hier Dateien
     vorhanden sind in der Behördenmappe, dass ich die auch anklicken kann." */
  const [dateiZu, setDateiZu] = useState<any>(null);
  const [dateiWeg, setDateiWeg] = useState<any>(null);
  const [zurueck, setZurueck] = useState<any>(null);
  const [versand, setVersand] = useState<any>(null);
  /* Welche SENDUNG offen ist: aaz, faz oder poller. */
  const [post, setPost] = useState<string | null>(null);
  const [antwort, setAntwort] = useState<string | null>(null);

  const darfErzeugen = darf(ich, 'dokument.erzeugen');
  const darfVersenden = darf(ich, 'dokument.versenden');
  const darfFreigeben = darf(ich, 'behoerdenpost.freigeben');
  /* WAS AUF FREIGABE WARTET (Migration 126). Beide Seiten sehen es: wer die
     Sendung gebaut hat, soll sehen, dass sie WARTET und nicht verschwunden
     ist - und wer freigeben darf, soll sie hier finden, ohne durch die
     Glocke zu gehen. */
  const [freigaben, setFreigaben] = useState<any[]>([]);
  const [ausgang, setAusgang] = useState<any[]>([]);
  const [ablehnung, setAblehnung] = useState<any | null>(null);
  const [pruefe, setPruefe] = useState<any | null>(null);

  async function laden() {
    try { setD(await api.dokumente(auftragId)); }
    catch (f) { setFehler(ausFehler(f)); }
    /* Ein Partner hat weder `dokument.versenden` noch das Freigaberecht; der
       Weg antwortet ihm mit 403, und das ist richtig. Die Liste bleibt dann
       leer und hält nichts auf. */
    if (darfVersenden || darfFreigeben) {
      let liste: any[] = [];
      try { liste = await api.freigaben(auftragId); } catch { liste = []; }
      setFreigaben(liste);
      try { setAusgang(await api.postausgang(auftragId)); } catch { setAusgang([]); }
      /* AUS DER GLOCKE: DIREKT INS PRÜFFENSTER (.233), siehe sprungmarke.ts. */
      const m = freigabeNehmen(auftragId);
      if (m && darfFreigeben) {
        const offen = liste.filter((f) => f.zustand === 'offen');
        const f = offen.find((x) => x.id === m.freigabeId)
          ?? offen.find((x) => x.sorte === 'faz') ?? offen[0];
        if (f) setPruefe(f);
      }
    }
  }
  useEffect(() => { laden(); }, [auftragId]);
  useFrisch(laden);
  /* SCHON IN DER MAPPE, UND DIE GLOCKE ZEIGT HIERHER (.317): neu laden,
     dabei nimmt `laden` die Marke und öffnet das Prüffenster. Bei einer
     anderen Akte tut die Mappe nichts; die wechselt ohnehin. */
  useEffect(() => {
    const hoer = (e: Event) => {
      if ((e as CustomEvent).detail?.auftragId === auftragId) laden();
    };
    window.addEventListener(FREIGABE_VORGEMERKT, hoer);
    return () => window.removeEventListener(FREIGABE_VORGEMERKT, hoer);
  }, [auftragId, darfVersenden, darfFreigeben]);

  async function tu(was: () => Promise<any>) {
    setLaeuft(true); setFehler(null);
    try { const e = await was(); await laden(); return e; }
    catch (f) { setFehler(ausFehler(f)); return null; }
    finally { setLaeuft(false); }
  }

  if (!d) return <div><Fehlerkasten lage={fehler} /></div>;
  if (d.hinweis && !d.dokumente.length && ich.istSub) {
    return <p className="leise">{d.hinweis}</p>;
  }

  const gueltig = d.dokumente.filter((x: any) => x.zustand !== 'ersetzt');
  /* ERSETZT UND ZURÜCKGEZOGEN SIND ZWEI VERSCHIEDENE DINGE, und beide tragen
     denselben Zustand: „gilt nicht mehr". Ein ERSETZTES Papier hat einen
     Nachfolger, ein ZURÜCKGEZOGENES hat einen GRUND und keinen. Wer sie
     zusammenwürfe, könnte hinterher nicht mehr sagen, ob jemand etwas neu
     gebaut oder etwas zurückgenommen hat. */
  const ersetzt = d.dokumente.filter(
    (x: any) => x.zustand === 'ersetzt' && !x.zurueckgezogen_am);
  const zurueckgezogen = d.dokumente.filter((x: any) => x.zurueckgezogen_am);
  /* WAS DER PUNKT ÜBER DEM TITEL WEISS. Beides steht weiter unten schon
     einmal in den Kacheln; es wird hier gerechnet und nicht dort abgeschrieben
     - 2 Rechnungen über dieselbe Frage laufen beim ersten Nachtrag
     auseinander, und dann sagt der Punkt etwas anderes als die Kachel. */
  const ohneDatei = gueltig.filter((x: any) => !x.datei);
  const imOrdner = gueltig.filter((x: any) => x.zustand === 'im_ordner');

  return (
    <div>
      <div className="reihe" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        {/* DER ZUSTANDSPUNKT VOR DEM TITEL - dieselbe Antwort wie im
            Systemzustand seit dem 01.09.2026 und im Aufmaß seit .126: er
            beantwortet „muss ich hier hinsehen", und das kommt vor dem Namen.
            Rot, solange ein Papier ohne Datei dasteht (es geht ohne Anhang
            nicht hinaus), orange, solange etwas im Ordner liegt und nicht
            versendet ist, grün, wenn alles heraus ist. */}
        <div className="reihe" style={{ gap: 8 }}>
          <span className={'zustandspunkt '
            + (ohneDatei.length ? 'hart'
             : imOrdner.length ? 'offen'
             : d.dokumente.length ? 'gut' : 'aus')}
                title={ohneDatei.length ? 'Papiere ohne Datei'
                  : imOrdner.length ? 'Liegt im Ordner, noch nicht versendet'
                  : d.dokumente.length ? 'Alles versendet'
                  : 'Noch kein Papier in der Mappe'} />
          <b>Behördenmappe</b>
        </div>
        {darfErzeugen && (
          <div className="reihe">
            <button className="knopf" onClick={() => setDialog('eingang')}>
              Eingang erfassen
            </button>
            <button className="knopf haupt" onClick={() => setDialog('erzeugen')}>
              Dokumente erzeugen
            </button>
            {/* DIE SENDUNGEN. Drei Knöpfe, drei Mails - nicht einer je Papier.
                Marvin, 03.09.2026: „es werden ja die entsprechenden dokumente
                zusammenversendet mit email, anhang usw."
                Der Pollerantrag steht getrennt, weil er an ein ANDERES Amt
                geht: der Bestand nennt den stillen Rückfall auf die Adresse
                der Aufgrabung „den gefaehrlichsten Fehler". */}
            <button className="knopf" onClick={() => setPost('aaz')}>
              AAZ/VAO senden
            </button>
            <button className="knopf" onClick={() => setPost('faz')}>
              FAZ senden
            </button>
            <button className="knopf" onClick={() => setPost('poller')}>
              Poller senden
            </button>
          </div>
        )}
      </div>

      <Fehlerkasten lage={fehler} />
      {antwort && <div className="hinweis">{antwort}</div>}

      {/* DIE SCHULUNGSSTUFE VOR DER BEHÖRDENPOST.
          ==================================================================
          ORIGINALTEXT MARVIN (03.09.2026)
            „eine temporäre prüfung vor mailversand an die behörde als
             kontrolle, wie zb. FAZ ... man kann sich die vorschau anschauen.
             hintergrund ist, dass schlechte fotos raus gehen und wir die
             leute erstmal schulen müssen."

          Sie steht GANZ OBEN und nicht am Ende der Seite: eine Sendung, die
          wartet, hält einen Antrag auf, und an dem hängt eine Frist. */}
      {/* ANORDNUNG OHNE ZEITRAUM (.245). Marvin: „das datum ist
          entscheidend, hat das system kein datum, kommt eine meldung dass
          die datei geprüft werden muss und dann haben wir einen button um
          den zeitraum einzutragen oder abgelaufen". */}
      {(d.zu_pruefen ?? []).length > 0 && (
        <AnordnungPruefen liste={d.zu_pruefen} auftragId={auftragId}
                          darf={darfErzeugen} fertig={(t) => { setAntwort(t); laden(); }} />
      )}

      {freigaben.filter((f) => f.zustand === 'offen').map((f) => (
        <div className="hinweis warnung" key={f.id}>
          <b>Wartet auf Freigabe: {f.sorte.toUpperCase()}</b>
          <div className="klein" style={{ marginTop: 4 }}>
            An {(f.empfaenger ?? []).join(', ') || '(kein Empfänger)'},
            {' '}angefordert von {f.angefordert_name ?? 'jemandem'} am
            {' '}{zeitpunkt(f.angefordert_am)}
          </div>
          {/* NACH EINER ABLEHNUNG: WAS SICH AN DEN FOTOS GETAN HAT (.229). */}
          {f.vergleich && (
            <div className="klein" style={{ marginTop: 6 }}>
              {f.vergleich.abgelehnt_grund && <div>Abgelehnt, weil: {f.vergleich.abgelehnt_grund}</div>}
              {(f.vergleich.neu ?? []).length > 0 && <div><b>Neu:</b> {f.vergleich.neu.join(', ')}</div>}
              {(f.vergleich.ersetzt ?? []).length > 0 && (
                <div><b>Ausgetauscht:</b> {f.vergleich.ersetzt.map((x: any) => x.neu).join(', ')}</div>)}
              {(f.vergleich.bisher ?? []).length > 0 && <div><b>Wie zuvor:</b> {f.vergleich.bisher.join(', ')}</div>}
              {(f.vergleich.weg ?? []).length > 0 && (
                <div><b>Nicht mehr im Ordner (geht nicht mit):</b> {f.vergleich.weg.join(', ')}</div>)}
            </div>
          )}
          {(f.anlagen ?? []).length > 0 && (
            <ul style={{ margin: '6px 0 0 18px' }} className="klein">
              {f.anlagen.map((a: string) => <li key={a}>{a}</li>)}
            </ul>
          )}
          <div className="reihe" style={{ marginTop: 8 }}>
            {/* DIE VORSCHAU IST DASSELBE FENSTER WIE BEIM SENDEN. Ein
                zweites „Freigabe ansehen" wäre eine zweite Bedienung für
                dieselbe Frage - und die zweite zeigte irgendwann etwas
                anderes. */}
            <button className="knopf klein"
                    disabled={!darfFreigeben}
                    title={darfFreigeben ? undefined
                      : 'Dafür fehlt das Recht „Behördenpost freigeben".'}
                    onClick={() => setPruefe(f)}>
              Vorschau ansehen und freigeben
            </button>
            <button className="knopf klein" disabled={!darfFreigeben}
                    onClick={() => setAblehnung(f)}>
              Ablehnen
            </button>
            {/* SCHON VERSENDET, AUSSERHALB (.324). Marvin: „da sind schon ein
                paar verschickt worden, das bringt wieder chaos." Ein Klick
                vermerkt es, die Freigabe fällt aus der Glocke, die Automatik
                schweigt für diese Baustelle. */}
            <button className="knopf klein" disabled={!darfFreigeben}
                    title="Die Post ging schon außerhalb der Plattform hinaus. Hier nur vermerken, nichts senden."
                    onClick={async () => {
                      if (!await fragen('Wurde diese Post wirklich schon außerhalb der Plattform versendet? Es wird nur vermerkt, nichts gesendet.')) return;
                      const e = await tu(() => api.freigabeSchonVersendet(f.id));
                      if (e) setAntwort(e.klartext);
                    }}>
              Schon versendet
            </button>
          </div>
        </div>
      ))}
      {/* DER POSTAUSGANG (.315). Marvin, 06.10.2026: „auch so weiß ich nicht
          ob es versendet wurde!" Jede Behördenmail dieses Auftrags, mit
          Empfänger, Kopie, Anlagen und Ergebnis. */}
      {ausgang.length > 0 && (
        <details className="hinweis" style={{ marginBottom: 12 }} open={ausgang.some((m) => !m.erfolgreich)}>
          <summary>
            <b>Postausgang an die Behörde ({ausgang.length})</b>
            , zuletzt {zeitpunkt(ausgang[0].am)}:{' '}
            <span className={`merker ${ausgang[0].erfolgreich ? (/Testadresse/.test(ausgang[0].ergebnis ?? '') ? 'warnung' : 'gruen') : 'fehler'}`}>
              {ausgang[0].erfolgreich ? (/Testadresse/.test(ausgang[0].ergebnis ?? '') ? 'an die Testadresse' : 'versendet') : 'NICHT versendet'}
            </span>
          </summary>
          <table className="liste klein" style={{ marginTop: 6 }}>
            <thead><tr><th>Wann</th><th>Was</th><th>An</th><th>Kopie</th><th>Anlagen</th><th>Ergebnis</th></tr></thead>
            <tbody>
              {ausgang.map((m) => (
                <tr key={m.id}>
                  <td>{zeitpunkt(m.am)}</td>
                  <td>{String(m.anlass ?? '').replace('behoerdenpost_', '').toUpperCase()}</td>
                  <td>{m.empfaenger}</td>
                  <td>{m.bcc || '-'}</td>
                  <td>{(m.dateien ?? []).join(', ') || '-'}</td>
                  <td>
                    <span className={`merker ${m.erfolgreich ? (/Testadresse/.test(m.ergebnis ?? '') ? 'warnung' : 'gruen') : 'fehler'}`}>
                      {m.erfolgreich ? (/Testadresse/.test(m.ergebnis ?? '') ? 'an die Testadresse' : 'versendet') : 'nicht versendet'}
                    </span>
                    {m.ergebnis && <div className="leise">{m.ergebnis}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {freigaben.filter((f) => f.zustand === 'abgelehnt').slice(0, 1).map((f) => (
        <div className="hinweis" key={f.id}>
          <b>Zuletzt nicht freigegeben:</b> {f.sorte.toUpperCase()} am
          {' '}{zeitpunkt(f.entschieden_am)} von
          {' '}{f.entschieden_name ?? 'jemandem'} - {f.grund}
        </div>
      ))}

      {/* WAS DER BILDSCHIRM AUF EINEN BLICK SAGEN MUSS.
          ==================================================================
          ORIGINALTEXT MARVIN (03.09.2026)
            „hier habe ich bereits schon mal gesagt du sollst das grau-in-grau
             entfernen. du sollst das dynamischer, innovativer und heller
             gestalten."

          Dieselbe Antwort wie beim Systemzustand am 01.09.2026: eine
          Kachelreihe mit den Zahlen, die man beim Öffnen sucht, und Farbe
          dort, wo etwas zu tun ist. Vorher stand hier eine Tabelle mit sechs
          gleich grauen Spalten - ob eine Frist läuft, sah man erst, nachdem
          man sie gelesen hatte.

          Eine Kachel, die immer null zeigt, steht gar nicht erst da. */}
      {(d.dokumente ?? []).length > 0 && (
        <div className="kachelreihe" style={{ marginBottom: 12 }}>
          <div className="kachel">
            <div className="wert">{d.dokumente.length}</div>
            <div className="was">
              {d.dokumente.length === 1 ? 'Papier' : 'Papiere'} in der Mappe
            </div>
          </div>
          <div className="kachel gut">
            <div className="wert">
              {d.dokumente.filter((x: any) => x.zustand === 'versendet').length}
            </div>
            <div className="was">versendet</div>
          </div>
          {(() => {
            const offen = d.dokumente.filter(
              (x: any) => x.quelle === 'eigen' && x.zustand !== 'versendet').length;
            return offen > 0 ? (
              <div className="kachel offen">
                <div className="wert">{offen}</div>
                <div className="was">liegen im Ordner, noch nicht versendet</div>
              </div>
            ) : null;
          })()}
          {/* DIESE KACHEL HAT NIE EIN MENSCH GESEHEN, UND ZWAR SEIT ES SIE
              GIBT. Sie filterte auf `x.restfrist` - eine Spalte, die dieser
              Weg nie geliefert hat -, also traf sie immer null Zeilen und
              zeichnete nichts. **Ein Filter über ein Feld, das gar nicht
              mitkommt, ist von einem Auftrag ohne Fristen nicht zu
              unterscheiden**, und deshalb hat es niemand gemeldet.
              Gerechnet wird jetzt aus der Ampel, die der Server mitschickt,
              und das Warnfenster ist die Zahl aus der Konfiguration statt
              einer festgenagelten 14. */}
          {(() => {
            const fristen = d.dokumente.filter((x: any) => laeuftNoch(x));
            const knapp = fristen.filter((x: any) => x.ampel === 'warnung');
            return fristen.length > 0 ? (
              <div className={'kachel' + (knapp.length ? ' hart' : '')}>
                <div className="wert">{fristen.length}</div>
                <div className="was">
                  {fristen.length === 1 ? 'laufende Frist' : 'laufende Fristen'}
                  {knapp.length > 0
                    && `, ${knapp.length} unter ${d.warnfenster_tage} Tagen`}
                </div>
              </div>
            ) : null;
          })()}
          {/* UND WENN DIE BAUSTELLE ABGEMELDET IST, STEHT DAS OBEN.
              Marvin, 14.09.2026: „Ist die FAZ geschrieben, entfällt die Info
              zur abgelaufenen VRAO". Die Fristen fallen damit aus der Kachel
              daneben - **eine Zahl, die still verschwindet, ist von einer
              verlorenen nicht zu unterscheiden**, also sagt diese Kachel,
              warum. */}
          {d.hat_faz && (
            <div className="kachel">
              <div className="wert">✓</div>
              <div className="was">
                abgemeldet, die Fertigstellungsanzeige liegt vor
              </div>
            </div>
          )}
          {(() => {
            const ohneDatei = d.dokumente.filter((x: any) => !x.datei).length;
            return ohneDatei > 0 ? (
              <div className="kachel offen">
                <div className="wert">{ohneDatei}</div>
                <div className="was">ohne Datei</div>
              </div>
            ) : null;
          })()}
        </div>
      )}

      {d.testmodus && (
        <div className="hinweis warnung">
          Der Testmodus ist an. Behördenpost geht an die Testadresse, nicht an
          die Gemeinde. Umschalten unter Konfiguration, Schlüssel
          <b> betrieb.testmodus</b>.
        </div>
      )}
      {!d.testmodus && !d.behoerde_versand && (
        <div className="hinweis warnung">
          Der Versand an Behörden ist gesperrt (<b>betrieb.behoerde_versand</b>).
          Erzeugen geht, Versenden nicht.
        </div>
      )}
      {d.gemeinde && !d.gemeinde.gepflegt && (
        <div className="hinweis warnung">
          Für {d.gemeinde.gemeinde ?? 'diese Gemeinde'} fehlen noch Stammdaten.
          Ohne Mailadresse oder Portalweg lässt sich nichts versenden.
        </div>
      )}
      {!d.eigene_vao_noetig && (
        <div className="hinweis">
          Für diesen Auftrag wird keine eigene verkehrsrechtliche Anordnung
          beantragt: er liegt auf privatem Grund oder hängt an einer Sammel-VAO.
        </div>
      )}

      {!gueltig.length && <p className="leise">Die Mappe ist noch leer.</p>}

      {gueltig.length > 0 && (
        <table className="tabelle">
          <thead>
            <tr>
              <th style={{ width: 90 }}>Art</th>
              <th style={{ width: 110 }}>Herkunft</th>
              <th>Datei</th>
              {/* DIE SPALTE „AKTENZEICHEN" IST WEG.
                  Marvin, 08.09.2026: „bei der behördenmappe kann die spalte
                  mit dem aktenzeichen raus."
                  Sie war 130 Bildpunkte breit und bei fast jedem Papier leer:
                  ein Aktenzeichen vergibt die Behörde, und unsere eigenen
                  Papiere tragen keines. Verloren geht nichts - es steht
                  weiter im Fenster „Nachtragen", also an der Stelle, an der
                  man es einträgt und nachsieht. */}
              <th style={{ width: 160 }}>Frist</th>
              <th style={{ width: 130 }}>Stand</th>
              <th style={{ width: 190 }} />
            </tr>
          </thead>
          <tbody>
            {gueltig.map((x: any) => (
              <tr key={x.id}>
                <td>{dokumentArt(x.art)}</td>
                <td>
                  {/* Herkunft schlaegt Inhalt: nur das Papier der Behoerde
                      traegt eine Frist. Dieselbe Marke wie in der Akte, damit
                      man sie an beiden Stellen wiedererkennt: MDS im
                      MDS-Blau, die Behoerde in Gruen. „wir" hiess sie bis zum
                      25.08.2026 und sagte niemandem etwas. */}
                  <span className={'herkunft ' + (x.quelle === 'behoerde'
                    ? 'behoerde' : 'mds')}>
                    {x.quelle === 'behoerde' ? 'Behörde' : 'MDS'}
                  </span>
                  {/* ZÄHLT NICHT ALS ANORDNUNG (Migration 313). Marvin,
                      23.09.2026: „es gab bzw gibt fehlende VRAOs ... also
                      warum greift hier nichts?" Ein Haken der alten Liste
                      trägt kein Papier; die alte Liste hat ihm selbst nicht
                      geglaubt. */}
                  {x.quelle === 'behoerde' && x.art === 'VAO' && x.belegt === false && (
                    <div>
                      <span className="marke-zustand z-ueberfaellig"
                            title="Zählt erst mit Datei der Behörde oder Aktenzeichen.">
                        {x.nur_haken ? 'nur Haken aus der alten Liste' : 'ohne Beleg'}
                      </span>
                    </div>
                  )}
                </td>
                <td>
                  {/* EINE SACKGASSE WIRD EIN WEG.
                      Hier stand viermal „keine Datei" und sonst nichts - und
                      das war keine kaputte Anzeige, sondern ein fehlender
                      Weg: die PDF liegt in der Ablage des Auftrags, nur zeigt
                      kein Bezug auf dieses Papier. Wer das liest, weiss nicht,
                      ob die Datei fehlt oder ob er sie nur nicht findet. */}
                  {/* UND AN EINEM EIGENEN PAPIER WIRD KEINE DATEI GESUCHT.
                      Marvin, 07.09.2026: „was ist der bereich «Datei für
                      fertigstellungsanszeige»? damit kann ich nichts
                      anfangen?" Bei einem BEHÖRDENpapier ist das der Weg, auf
                      dem ein hereingekommener Scan sein Papier findet; bei
                      einem EIGENEN entsteht die Datei mit dem Papier - dort
                      ist es eine Frage, auf die es nur eine falsche Antwort
                      gibt. */}
                  {x.datei
                    ? <>
                        <a href={api.dateiAdresse(x.datei, x.dateiname)} target="_blank"
                           rel="noreferrer">{x.dateiname ?? 'Datei'}</a>
                        {darfErzeugen && x.quelle === 'behoerde' && (
                          <button className="kartenweg" style={{ marginLeft: 8 }}
                                  onClick={() => setDateiZu(x)}>ändern</button>
                        )}
                        {darfErzeugen && (
                          /* „LÖSCHEN" HEISST HIER ZWEIERLEI, und beides steht
                             jetzt da: die Datei aus dem Auftrag nehmen (sie
                             liegt danach im Papierkorb des Dateireiters und
                             lässt sich zurückholen). */
                          <button className="kartenweg" style={{ marginLeft: 8 }}
                                  onClick={() => setDateiWeg(x)}>
                            in den Papierkorb
                          </button>
                        )}
                      </>
                    : darfErzeugen && x.quelle === 'behoerde'
                    ? <button className="kartenweg" style={{ marginLeft: 0 }}
                              onClick={() => setDateiZu(x)}>
                        Datei zuordnen
                      </button>
                    : x.quelle !== 'behoerde'
                    ? <span className="leise">
                        keine Datei - sie entsteht mit dem Papier
                      </span>
                    : <span className="leise">{x.dateiname ?? 'keine Datei'}</span>}
                  {/* DIE GEBÜHR SAGT, WOHER SIE KOMMT - wie die Laufzeit.
                      Marvin, 08.09.2026: „auch der hinweis, dass die gebühr
                      aus der eingelesenen VAO erkannt wurde."
                      Der Unterschied ist keine Kleinigkeit: eine Zahl, die
                      ein MENSCH aus dem Bescheid abgeschrieben hat, ist
                      geprüft; eine, die der Server aus dem Text gelesen hat,
                      ist ein Vorschlag - und sie geht über die Position
                      10099980 in die Erstattung. Wer den Unterschied nicht
                      sieht, hält beide für gleich sicher. */}
                  {/* GEBÜHRENBESCHEID ERWARTET (.306): blau, wie bei den Gebühren. */}
                  {x.gebuehr_erwartet && (
                    <div className="klein" style={{ marginTop: 4 }}>
                      <span className="merker erwartet">Gebührenbescheid erwartet</span>
                    </div>
                  )}
                  {x.gebuehr_betrag != null && (
                    <div className="klein leise">
                      Gebühr {euro(x.gebuehr_betrag)} Euro
                      {x.automatisch_erfasst && (
                        <span className="merker" style={{ marginLeft: 6 }}>
                          aus dem Bescheid gelesen
                        </span>
                      )}
                    </div>
                  )}
                  {/* UND WAS NICHT ZU LESEN WAR, STEHT AUCH DA.
                      Ein Papier ohne Gebühr sieht sonst aus wie eines, das
                      keine trägt - und das ist etwas anderes als „hier stand
                      etwas, das der Server nicht deuten konnte". */}
                  {x.automatisch_erfasst && x.gelesen_befund && (
                    <div className="klein warnfarbe">{x.gelesen_befund}</div>
                  )}
                </td>
                <td className="klein">
                  {x.gueltig_bis
                    ? <Frist von={x.gueltig_von} bis={x.gueltig_bis} lage={x} />
                    : <span className="leise">
                        {x.quelle === 'behoerde' ? 'ohne Frist' : '-'}
                      </span>}
                  {/* DERSELBE MERKER WIE AN DER GEBÜHR, UND DAS IST KEINE
                      Wiederholung: `automatisch_erfasst` ist eine Aussage
                      über das PAPIER und nicht über ein einzelnes Feld. Wer
                      ihn nur an einer der beiden Zahlen zeigte, behauptete
                      damit, die andere habe ein Mensch abgeschrieben. */}
                  {x.automatisch_erfasst && x.gueltig_bis && (
                    <div><span className="merker">aus dem Bescheid gelesen</span></div>
                  )}
                </td>
                <td className="klein">
                  {ZUSTAND[x.zustand] ?? x.zustand}
                  {x.versendet_am && (
                    <div className="leise">{datum(x.versendet_am)}</div>
                  )}
                </td>
                <td>
                  <div className="reihe">
                    {darfVersenden && x.quelle === 'eigen'
                      && x.zustand !== 'versendet' && (
                      <button className="knopf klein" disabled={laeuft}
                              onClick={() => setVersand(x)}>
                        versenden
                      </button>
                    )}
                    {darfErzeugen && (
                      <Aendern x={x} laeuft={laeuft} fehler={fehler}
                               speichere={(b) => tu(() => api.dokumentAendern(x.id, b))} />
                    )}
                    {darfErzeugen && (
                      <button className="knopf klein warnung" disabled={laeuft}
                              onClick={() => setZurueck(x)}>
                        zurückziehen
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ZURÜCKGEZOGEN HEISST VOM BILDSCHIRM UND NICHT AUS DER WELT. Die
          Zeile steht mit ihrem GRUND da - eine Frist, die aus dem Werk fällt,
          ohne dass jemand sagen kann warum, ist von einer verlorenen nicht zu
          unterscheiden. */}
      {zurueckgezogen.length > 0 && (
        <details style={{ marginTop: 16 }}>
          <summary className="klein leise">
            {zurueckgezogen.length} zurückgezogen{zurueckgezogen.length === 1
              ? 'es Papier' : 'e Papiere'}. Sie gelten nicht mehr - die Zeile
            bleibt mit Datum, Person und Grund stehen.
          </summary>
          <table className="tabelle" style={{ marginTop: 8 }}>
            <tbody>
              {zurueckgezogen.map((x: any) => (
                <tr key={x.id}>
                  <td style={{ width: 90 }}>{dokumentArt(x.art)}</td>
                  <td>
                    {x.zurueckgezogen_grund}
                    <div className="klein leise">
                      {zeitpunkt(x.zurueckgezogen_am)}
                      {x.zurueckgezogen_name ? `, ${x.zurueckgezogen_name}` : ''}
                    </div>
                  </td>
                  <td className="klein leise" style={{ width: 150 }}>
                    {x.dateiname ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {ersetzt.length > 0 && (
        <details style={{ marginTop: 16 }}>
          <summary className="klein leise">
            {ersetzt.length} ersetzte {ersetzt.length === 1 ? 'Fassung' : 'Fassungen'}.
            Sie bleiben stehen: was einmal an eine Behörde ging, verschwindet nicht.
          </summary>
          <table className="tabelle" style={{ marginTop: 8 }}>
            <tbody>
              {ersetzt.map((x: any) => (
                <tr key={x.id}>
                  <td>{dokumentArt(x.art)}</td>
                  <td>
                    {x.datei
                      ? <a href={api.dateiAdresse(x.datei, x.dateiname)}
                           target="_blank" rel="noreferrer">{x.dateiname}</a>
                      : x.dateiname}
                  </td>
                  <td className="klein leise">{zeitpunkt(x.erstellt_am)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {dialog === 'erzeugen' && (
        <Erzeugen
          auftragId={auftragId} eigeneVao={d.eigene_vao_noetig}
          laeuft={laeuft} fehler={fehler} zu={() => setDialog(null)}
          /* UND DANACH GEHT DIE SENDUNG AUF, NICHT DAS EINZELNE PAPIER.
             ================================================================
             ORIGINALTEXT MARVIN (03.09.2026)
               „ich will ja nicht jedes dokument einzelt versenden, so wie du
                es hier unter jeder erstellung eingefügt hast, sondern es
                werden ja die entsprechenden dokumente zusammenversendet mit
                email, anhang usw. da passt dein aufbau nicht."

             In .86 stand hier ein Versandfenster JE PAPIER - gut gemeint und
             fachlich falsch. Der Bestand kannte ZWEI Mails je Auftrag: die
             AAZ/VAO-Mail mit vier Anhängen (AAZ, Lageplan, Regelplan, VAO)
             und die FAZ-Mail mit der Anzeige und den Fotos. **Eine
             Aufgrabungsanzeige ohne den Lageplan kann die Gemeinde nicht
             bearbeiten** - wer sie einzeln schickt, schickt sie unvollständig.

             Deshalb öffnet sich jetzt die SENDUNG, zu der dieses Papier
             gehört, mit allem darin. */
          erzeuge={async (b, dannVersenden) => {
            const e = await tu(() => api.dokumentErzeugen(auftragId, b));
            if (!e) return;
            setAntwort(e.hinweis);
            setDialog(null);
            if (dannVersenden) {
              setPost(b.art === 'FAZ' ? 'faz'
                : b.art === 'POLLER' ? 'poller' : 'aaz');
            }
          }}
        />
      )}

      {dialog === 'eingang' && (
        <Eingang
          laeuft={laeuft} fehler={fehler} zu={() => setDialog(null)}
          erfasse={async (b) => {
            const e = await tu(() => api.dokumentEingang(auftragId, b));
            if (e) {
              setAntwort(e.restfrist != null
                ? `Erfasst. Die Frist läuft noch ${e.restfrist} Tage.`
                : 'Erfasst.');
              setDialog(null);
            }
          }}
        />
      )}

      {post && (
        <Sendung
          auftragId={auftragId} sorte={post} testmodus={d.testmodus}
          zu={() => setPost(null)}
          fertig={(satz: string) => { setAntwort(satz); setPost(null); laden(); }}
        />
      )}

      {pruefe && (
        <Sendung
          auftragId={auftragId} sorte={pruefe.sorte} testmodus={d.testmodus}
          freigabe={pruefe.id} wahlVorgabe={pruefe.wahl ?? {}}
          partner={pruefe.partner_firma ?? null}
          zu={() => setPruefe(null)}
          abgelehnt={(satz: string) => { setAntwort(satz); setPruefe(null); laden(); }}
          fertig={(satz: string) => { setAntwort(satz); setPruefe(null); laden(); }}
        />
      )}

      {ablehnung && (
        <Ablehnung
          x={ablehnung} laeuft={laeuft} fehler={fehler}
          zu={() => setAblehnung(null)}
          lehneAb={async (grund: string) => {
            const e = await tu(async () => api.freigabeAblehnen(ablehnung.id, grund, await fragen('Den Kommentar per Telegram an die Firma pushen?\n\nOK = ja, pushen\nAbbrechen = nein, nicht pushen')));
            if (e) { setAntwort(e.klartext); setAblehnung(null); }
          }}
        />
      )}

      {versand && (
        <Versenden
          x={versand} gemeinde={d.gemeinde} empfaenger={d.empfaenger}
          testmodus={d.testmodus}
          laeuft={laeuft} fehler={fehler} zu={() => setVersand(null)}
          sende={async (b) => {
            const e = await tu(() => api.dokumentVersenden(versand.id, b));
            if (e) { setAntwort(e.klartext); setVersand(null); }
          }}
          portal={async (az) => {
            const e = await tu(() => api.dokumentPortal(versand.id,
                                                        { aktenzeichen: az }));
            if (e) { setAntwort('Als über das Portal eingestellt vermerkt.');
                     setVersand(null); }
          }}
        />
      )}

      {dateiZu && (
        <DateiZuordnen
          x={dateiZu} auftragId={auftragId} laeuft={laeuft} fehler={fehler}
          zu={() => setDateiZu(null)}
          setze={async (bezugId) => {
            const e = await tu(() => api.dokumentDatei(dateiZu.id, bezugId));
            if (e) {
              setAntwort(bezugId
                ? `„${e.dateiname}" hängt jetzt an diesem Papier.`
                : 'Die Verknüpfung ist gelöst.');
              setDateiZu(null);
            }
          }}
        />
      )}

      {dateiWeg && (
        <MitGrund
          titel={`Datei in den Papierkorb`}
          untertitel={dateiWeg.dateiname ?? undefined}
          satz={'Die Datei geht aus dem Auftrag. Im Reiter „Dateien" steht sie '
              + 'unten im Papierkorb und lässt sich zurückholen; der Inhalt '
              + 'bleibt liegen.'}
          knopf="In den Papierkorb" pflicht={false}
          laeuft={laeuft} fehler={fehler} zu={() => setDateiWeg(null)}
          tuEs={async (grund) => {
            const e = await tu(() => api.dateiEntfernen(dateiWeg.datei, grund));
            if (e) {
              setAntwort('Die Datei liegt im Papierkorb des Auftrags.');
              setDateiWeg(null);
              laden();
            }
          }}
        />
      )}

      {zurueck && (
        <MitGrund
          titel={`${dokumentArt(zurueck.art)} zurückziehen`}
          untertitel={zurueck.aktenzeichen ?? undefined}
          satz={'Das Papier gilt danach nicht mehr: es fällt aus der Mappe, '
              + 'aus der Behördensendung und aus dem Fristenwerk. Die Zeile '
              + 'bleibt mit Datum, Person und Grund stehen, und die Datei '
              + 'bleibt im Auftragsordner.'}
          knopf="Zurückziehen" pflicht
          laeuft={laeuft} fehler={fehler} zu={() => setZurueck(null)}
          tuEs={async (grund) => {
            const e = await tu(() => api.dokumentZurueckziehen(zurueck.id, grund));
            if (e) {
              setAntwort(e.schon
                ? 'Dieses Papier war schon zurückgezogen.'
                : 'Das Papier ist zurückgezogen. Es steht unten mit seinem '
                  + 'Grund.');
              setZurueck(null);
              laden();
            }
          }}
        />
      )}
    </div>
  );
}


/**
 * Eine Frist mit ihrer Restlaufzeit. Eine Zahl allein sagt hier nichts.
 *
 * GERECHNET WIRD SIE AUF DEM SERVER, HIER WIRD NUR GESCHRIEBEN. Bis zum
 * 14.09.2026 stand hier eine ZWEITE Fassung derselben Rechnung: Resttage aus
 * der Uhr des Betrachters, eine festgenagelte 7 als Warnfenster, und die
 * Fertigstellungsanzeige kam darin gar nicht vor. **Zwei Fassungen derselben
 * Rechnung laufen beim ersten Nachtrag auseinander** - und dann steht
 * dieselbe Frist auf dem einen Bildschirm rot und auf dem anderen grün.
 *
 * Der Wortlaut steht in `teile/fristtext.ts`, ohne JSX daneben: eine Datei
 * mit JSX lässt sich mit `--experimental-strip-types` nicht laden, und eine
 * Textsuche misst, dass etwas dasteht, nicht, dass es stimmt.
 */
function Frist(
  { von, bis, lage }: { von: string | null; bis: string; lage: Fristlage }
) {
  const satz = fristText(lage);
  return (
    <span>
      {von ? datum(von) + ' bis ' : 'bis '}{datum(bis)}
      {satz && (
        <span className={'merker ' + fristKlasse(lage)}>{satz}</span>
      )}
    </span>
  );
}


/**
 * Dokumente erzeugen.
 *
 * ORIGINALTEXT MARVIN (02.09.2026)
 *   „1. das heißt "Dokumente erzeugen" auf dem registerblatt ist es auch
 *    falsch, 2. alle eingaben, die du hier machen kannst sind fehlerhaft. wir
 *    brauchen bei allen 4 dokumenarten und dann auch bei den verschiedenen
 *    VAOs alle felder zu den platzhaltern, so wie vorher auch. sonst kann man
 *    das nicht richtig befüllen und erzeugen. und dann zum manuellen erzeugen
 *    muss es auch dann an die richtige gemeinde versendet werden können, wenn
 *    alles erzeugt ist"
 *
 * DIE FELDLISTE STAND IM PROGRAMMCODE, und das war der ganze Fehler. Sieben
 * Felder von Hand (Beginn, Ende, Länge, Fläche, Verkehrsführung,
 * Fertigstellung, Aktenzeichen) gegen einen Katalog mit 131 Marken - und
 * welche davon dieses Papier braucht, hängt an der ART und am VORDRUCK der
 * Gemeinde. Beides weiss der Server; er liefert die Liste, die Oberfläche baut
 * sie nicht nach. Genau so macht es „Auftrag vorbereiten" seit dem
 * 27.08.2026, und dass es hier anders war, ist der Grund, warum sich Papiere
 * nicht richtig befüllen ließen.
 *
 * WAS DAS SYSTEM WEISS, WIRD NICHT GEFRAGT. Ein `auto`-Feld steht mit seinem
 * Wert da und lässt sich nicht ändern: eine Frage nach etwas, das danebensteht,
 * ist keine Sorgfalt. Was ein Mensch unter „Auftrag vorbereiten" eingetragen
 * hat, steht vorbelegt drin und trägt einen Vermerk, woher es kommt.
 */
function Erzeugen({ auftragId, eigeneVao, zu, erzeuge, laeuft, fehler }: any) {
  const [art, setArt] = useState('AAZ');
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState<Record<string, string>>({});
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const setze = (k: string, v: string) => setF((alt) => ({ ...alt, [k]: v }));

  /* DIE FELDER KOMMEN MIT DER ART. Wechselt sie, ist es ein anderes Papier
     mit anderen Marken - und die Eingaben von vorher gehören nicht hinein. */
  useEffect(() => {
    let weg = false;
    setD(null); setLadefehler(null);
    api.dokumentFelder(auftragId, art).then((v: any) => {
      if (weg) return;
      setD(v);
      const vor: Record<string, string> = {};
      for (const p of v.felder) if (p.wert) vor[p.feld] = p.wert;
      setF(vor);
    }).catch((e: any) => {
      if (!weg) setLadefehler(e?.fehler ?? 'Die Feldliste kam nicht.');
    });
    return () => { weg = true; };
  }, [auftragId, art]);

  /* ANSEHEN, BEVOR ES IN DEN ORDNER GEHT.
   *
   * Sie geht seit .86 denselben Weg wie das Erzeugen: liegt ein Vordruck der
   * Gemeinde da, zeigt die Vorschau IHN. Vorher zeigte sie immer das selbst
   * gebaute Papier - eine Vorschau, die etwas anderes zeigt als das, was
   * hinausgeht, ist keine.
   *
   * Die Blob-Adresse wird beim Schließen wieder freigegeben, sonst hängt das
   * Papier bis zum Neuladen der Seite im Speicher. */
  const [schau, setSchau] = useState<string | null>(null);
  const [schaut, setSchaut] = useState(false);
  const [schaufehler, setSchaufehler] = useState<string | null>(null);
  /* Das Fenster liest die Vorschau noch, wenn dieses Bauteil schon zu ist:
     freigegeben wird sie erst nach einer Minute. */
  useEffect(() => () => { if (schau) setTimeout(() => URL.revokeObjectURL(schau), 60000); }, [schau]);

  /* DIE VORSCHAU GEHT IM PDF-PROGRAMM DES GERÄTS AUF (.234), nicht mehr
     eingebettet in diesem Fenster; siehe teile/extern.ts. */
  async function vorschau() {
    setSchaut(true); setSchaufehler(null);
    try {
      await externSpaeter(async () => {
        const url = await api.dokumentVorschau(auftragId, { art, felder: f });
        setSchau(url);
        return url;
      });
    }
    catch (e: any) { setSchaufehler(e?.message ?? 'Die Vorschau ging nicht.'); }
    finally { setSchaut(false); }
  }

  const gruppen: string[] = d
    ? (d.gruppen ?? []).filter((g: string) =>
        d.felder.some((p: any) => p.gruppe === g))
    : [];
  const ohneGruppe = d
    ? d.felder.filter((p: any) => !gruppen.includes(p.gruppe)) : [];
  const fehltPflicht = d
    ? d.felder.filter((p: any) => p.pflicht && p.eingabe_typ !== 'auto'
                                  && !(f[p.feld] ?? '').trim())
    : [];

  return (
    <Dialog titel="Dokumente erzeugen" laeuft={laeuft} zu={zu} breite={780}>
      <Fehlerkasten lage={fehler} />
      <Auswahl name="art" titel="Was soll erzeugt werden?" pflicht wert={art}
               setze={setArt} leerText="" breite="100%" fehler={fehler}
               optionen={ERZEUGBAR.filter((e) => e.wert !== 'VAO' || eigeneVao)} />

      {ladefehler && <div className="hinweis fehler">{ladefehler}</div>}
      {!d && !ladefehler && <p className="klein leise">Die Felder werden geholt.</p>}

      {d && (
        <>
          {/* WELCHER VORDRUCK ES BAUT, GEHÖRT VOR DIE FELDER. Er entscheidet,
              welche Marken überhaupt gebraucht werden - und ohne ihn entsteht
              ein anderes Papier. */}
          <p className="klein leise" style={{ marginTop: 0 }}>
            {d.vordruck
              ? <>Vordruck: <b>{d.vordruck.name}</b> ({d.vordruck.dateiname})</>
              : d.hinweis}
          </p>

          <div className="bereichsleib">
            {[...gruppen, ...(ohneGruppe.length ? ['Weitere Angaben'] : [])]
              .map((g) => {
              const liste = g === 'Weitere Angaben' ? ohneGruppe
                : d.felder.filter((p: any) => p.gruppe === g);
              if (!liste.length) return null;
              return (
                <div key={g} style={{ marginBottom: 6 }}>
                  <h4>{g}</h4>
                  <div className="feldreihe">
                    {liste.map((p: any) => (
                      <Feldeingabe key={p.feld} p={p} wert={f[p.feld] ?? ''}
                                   setze={(v: string) => setze(p.feld, v)}
                                   fehler={fehler} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* DER GESPERRTE KNOPF NENNT DIE FEHLENDE ANGABE. Ein gesperrter
              Knopf ohne Grund sieht aus wie ein kaputter Knopf. */}
          {fehltPflicht.length > 0 && (
            <div className="hinweis warnung">
              <b>Es fehlen Pflichtangaben.</b>
              {' '}{fehltPflicht.map((p: any) => p.beschriftung).join(', ')}
            </div>
          )}
        </>
      )}

      <p className="klein leise">
        Das Papier wird als PDF im Auftragsordner abgelegt. Versendet wird es
        damit nicht - danach steht der Weg dorthin offen.
      </p>
      {schaufehler && <div className="hinweis fehler">{schaufehler}</div>}
      {schau && (
        <p className="klein leise" style={{ marginTop: 6 }}>
          Die Vorschau ist in einem eigenen Fenster aufgegangen. Sie liegt
          nirgends; erst „Erzeugen" legt das Papier im Auftragsordner ab.
        </p>
      )}
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt" disabled={laeuft || !d}
                onClick={() => erzeuge({ art, felder: f, ...f })}>
          {laeuft ? 'Einen Moment' : 'Erzeugen'}
        </button>
        <button className="knopf haupt" disabled={laeuft || !d}
                onClick={() => erzeuge({ art, felder: f, ...f }, true)}>
          Erzeugen und versenden
        </button>
        <button className="knopf" disabled={laeuft || schaut || !d}
                onClick={vorschau}>
          {schaut ? 'Einen Moment' : schau ? 'Vorschau neu' : 'Erst ansehen'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


/**
 * Ein einzelnes Feld des Katalogs.
 *
 * WAS DAS SYSTEM WEISS, WIRD NICHT GEFRAGT. Ein `auto`-Feld steht mit seinem
 * Wert da und lässt sich nicht ändern - sonst wäre es eine Frage nach etwas,
 * das danebensteht, und beim zwanzigsten Mal tippt jemand etwas anderes
 * hinein als in der Akte steht.
 */
function Feldeingabe({ p, wert, setze, fehler }: any) {
  const hinweis = [
    p.hinweis,
    p.woher === 'eingetragen' ? 'aus „Auftrag vorbereiten"' : null,
    p.woher === 'auto' ? 'rechnet das System' : null,
    p.woher === 'standard' ? 'Standardwert des Katalogs' : null,
  ].filter(Boolean).join(', ');

  if (p.eingabe_typ === 'auto') {
    return (
      <Text name={p.feld} titel={p.beschriftung} gesperrt breite={260}
            wert={wert} setze={() => {}} fehler={fehler} hinweis={hinweis} />
    );
  }
  if (p.eingabe_typ === 'checkbox') {
    return (
      <Haken name={p.feld} titel={p.beschriftung}
             wert={wert === 'angekreuzt' || wert === 'ja'}
             setze={(v: boolean) => setze(v ? 'angekreuzt' : 'leer')} />
    );
  }
  if (p.art === 'liste' || (p.liste ?? []).length) {
    return (
      <Auswahl name={p.feld} titel={p.beschriftung} pflicht={p.pflicht}
               breite={260} wert={wert} setze={setze} fehler={fehler}
               hinweis={hinweis}
               optionen={(p.liste ?? []).map((x: string) =>
                 ({ wert: x, text: x }))} />
    );
  }
  const art = p.art === 'datum' ? 'date' : undefined;
  const lang = p.art === 'text' && p.reihenfolge >= 30 && p.reihenfolge < 40;
  return (
    <Text name={p.feld} titel={p.beschriftung} pflicht={p.pflicht}
          art={art} mehrzeilig={lang} breite={lang ? '100%' : 260}
          wert={wert} setze={setze} fehler={fehler} hinweis={hinweis} />
  );
}


/**
 * Ein Anhang als Kachel: was die gemeinsame Kachel über ihn wissen muss.
 *
 * DIE KENNUNG KOMMT AUS `bezugId` UND NICHT AUS DEM FACH. Im Fach steht die
 * Kennung der DATEI (`FOTO:<datei_id>`), und darunter liegt der Wahlschlüssel,
 * den eine wartende Freigabe als JSON mit sich trägt; die Adresse
 * `/api/dateien/:bezugId` liest dagegen den DATEIBEZUG. **Zwei Kennungen, die
 * beide „die Datei" heissen, sind zwei Kennungen** - wer hier die falsche
 * nimmt, bekommt einen 404, und ein Vorschaubild, das nicht kommt, sieht aus
 * wie eine Datei, die es nicht gibt.
 */
function kacheldatei(a: any): Kacheldatei {
  return {
    id: String(a.bezugId ?? ''),
    dateiname: a.dateiname ?? '',
    mime: a.mime ?? null,
    groesse: a.bytes ?? null,
  };
}

/** Die Kennung der Datei, unter der die Wahl dieses Fachs steht. */
function dateiAusFach(fach: string): string {
  return fach.slice(fach.indexOf(':') + 1);
}

/**
 * EIN GITTER AUS ANLAGEN, DIE MAN AN- UND ABWÄHLT.
 *
 * ORIGINALTEXT MARVIN (08.09.2026, Punkt 21)
 *   „Behördenpost: weitere Anhänge hinzufügen mit Vorschaubildern (Auswahl
 *    oder Dateiablage)"
 *
 * ZWEI ABSCHNITTE, EIN BAUTEIL. Die Fotos der Fertigstellung und die weiteren
 * Anhänge stellen dieselbe Frage - „geht diese Datei mit?" - und sie
 * unterscheiden sich in genau zwei Dingen: was ab Werk gewählt ist (das
 * entscheidet der Server) und ob der Ordner darunter steht. **Beides sind
 * Eigenschaften und keine zweite Fassung**; zwei Abschriften liefen beim
 * ersten Nachtrag auseinander, und dann trägt ein Foto ein Vorschaubild und
 * ein Regelplan keines.
 *
 * DER SCHLÜSSEL IST DAS FACH UND NICHT DIE DATEIKENNUNG. Hier stand für die
 * Fotos `setWahl((w) => ({ ...w, [id]: … }))`, also die blanke Kennung; jedes
 * andere Fach dieses Fensters steht unter seinem FACHNAMEN. Eine Wahl, die je
 * nach Sorte anders geschrieben wird, muss auf der Gegenseite zweimal gelesen
 * werden - und beim dritten Mal einmal zu wenig.
 */
function Anlagengitter({
  anlagen, setzeFach, zeige, mitOrdner,
}: {
  anlagen: any[];
  setzeFach: (fach: string, wert: string) => void;
  zeige: (d: Kacheldatei, liste?: Kacheldatei[]) => void;
  /** Unter dem Namen steht, aus welchem Ordner die Datei kommt. */
  mitOrdner?: boolean;
}) {
  /* GEBLÄTTERT WIRD DURCH DIESEN ABSCHNITT. Wer ein Foto der Fertigstellung
     aufmacht, sieht die Fotos durch und nicht die Bauakte dazwischen. */
  const liste = anlagen.map(kacheldatei);

  return (
    <div className="dateigitter">
      {anlagen.map((a: any) => {
        const datei = kacheldatei(a);
        const an = !!a.dateiId;
        return (
          <Dateikachel key={a.fach} datei={datei} gewaehlt={an}
                       oeffne={(x) => zeige(x, liste)}>
            {/* DAS HÄKCHEN LIEGT NEBEN DEM WEG ZUR DATEI UND NICHT DARIN.
                Ein Knopf in einem Knopf ist im Browser nicht erlaubt, und ein
                Klick auf die Kachel soll die Datei ZEIGEN und nicht die Wahl
                umlegen: wer nachsehen will, was er da mitschickt, hätte sie
                sonst mit dem Nachsehen abgewählt. */}
            <label className="kachelwahl">
              <input type="checkbox" checked={an}
                     onChange={(e) => setzeFach(
                       a.fach,
                       e.target.checked ? dateiAusFach(a.fach) : '')} />
              {' '}<span>{an ? 'geht mit' : 'mitschicken'}</span>
            </label>

            {/* WAS SEIT DER ABLEHNUNG NEU IST (.229). */}
            {a.marke && (
              <span className={'merker ' + (a.marke === 'bisher' ? 'leise' : a.marke === 'neu' ? 'gruen' : 'blau')}
                    title={a.marke === 'neu' ? 'Nach der Ablehnung neu dazugekommen'
                      : a.marke === 'ersetzt' ? 'Unter demselben Namen ausgetauscht'
                      : 'War schon in der abgelehnten Sendung'}>
                {a.marke === 'neu' ? 'neu' : a.marke === 'ersetzt' ? 'ausgetauscht' : 'wie zuvor'}
              </span>
            )}
            {mitOrdner && (
              <span className="klein leise kachelordner"
                    title={a.beschriftung}>{a.beschriftung}</span>
            )}
          </Dateikachel>
        );
      })}
    </div>
  );
}


/**
 * DIE SENDUNG AN DIE GEMEINDE - eine Mail, alle Anlagen.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „es werden ja die entsprechenden dokumente zusammenversendet mit email,
 *    anhang usw."
 *
 * **DIE DATEINAMEN STEHEN DARIN UND NICHT NUR IHRE ANZAHL.** Der Bestand hat
 * es genau so gemacht und den Grund dazugeschrieben: „Vier Dateien zu senden,
 * von denen eine die falsche ist, faellt sonst niemandem auf." Je Fach ein
 * Auswahlfeld über alle Dateien des Auftrags, mit dem gefundenen vorbelegt -
 * eine automatische Zuordnung, die man nicht übergehen kann, ist eine
 * Behauptung.
 */
/* UND DASSELBE FENSTER IST DIE VORSCHAU EINER WARTENDEN FREIGABE.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „man kann sich die vorschau anschauen"
 *
 * Ein zweites Fenster „Freigabe ansehen" wäre eine zweite Bedienung für
 * dieselbe Frage - und die zweite zeigte irgendwann etwas anderes als die
 * erste. Mit `freigabe` wechselt genau zweierlei: der Knopf heisst
 * „Freigeben und senden", und er ruft den Weg, der die wartende Zeile
 * abschliesst. Die WAHL kommt aus der Zeile, damit dieselben Anlagen
 * dastehen, die der Innendienst gewählt hat. */
function Sendung({ auftragId, sorte, testmodus, zu, fertig,
                   freigabe = null, wahlVorgabe = null,
                   partner = null, abgelehnt = null }: any) {
  /* ABLEHNEN MIT KOMMENTAR IM SELBEN FENSTER (.233). Marvin: „wenn ich
     ablehne brauche ich ein Kommentarfeld das dann beim absenden per
     telegram an den Partner geht direkt." Wer die Fotos gerade sieht, soll
     nicht in ein zweites Fenster wechseln, um zu sagen, was an ihnen fehlt. */
  const [ablehnen, setAblehnen] = useState(false);
  const [kommentar, setKommentar] = useState('');
  const [d, setD] = useState<any>(null);
  const [wahl, setWahl] = useState<Record<string, string | null>>(
    wahlVorgabe ?? {});
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<any>(null);
  const [frage, setFrage] = useState(false);
  /* .546: nach dem Riegel „Anordnung deckt den Termin" die ausdrückliche Bestätigung. */
  const [trotzAnordnung, setTrotzAnordnung] = useState(false);
  /* KEIN TIEFBAU IM ÖFFENTLICHEN BEREICH (.341). ORIGINALTEXT MARVIN
     (10.10.2026): „dann stellen wir das vor Ort fest und dann müssen wir das
     bei der Abmeldung auf jeden Fall kennzeichnen, damit die andere
     Textvorlage verwendet wird." `null` heißt: noch nicht angefasst, es
     gilt, was die Akte sagt; der Server meldet den geltenden Stand zurück. */
  const [keinTb, setKeinTb] = useState<boolean | null>(null);

  /* DER HAKEN STEHT AM ANFANG DES BAUTEILS und läuft bei jedem Durchgang.
     Weiter unten steht eine bedingte Rückgabe („wird zusammengestellt"), und
     ein Haken dahinter ist auf dem Telefon ein weisser Bildschirm
     (React-Fehler 310) - das hat der Burger am 05.09.2026 gekostet. */
  const { fenster: dateifenster, zeige: zeigeDatei } = useDateifenster();

  async function laden(w: Record<string, string | null>, vorlageNeu = false) {
    try {
      const r = await api.behoerdenpostVorschau(auftragId, sorte, w, keinTb ?? undefined);
      setD(r);
      /* MIT DEM HAKEN WECHSELT DIE VORLAGE: dann kommen Betreff und Text neu,
         auch wenn schon etwas dastand. */
      if (!betreff || vorlageNeu) setBetreff(r.betreff);
      if (!text || vorlageNeu) setText(r.text);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  /* AUCH HIER KEIN `useFrisch`: `laden` schreibt Betreff und Text in die
     Felder, in denen der Innendienst gerade formuliert. Ein Nachzug mitten
     im Satz nähme ihm den Satz. */
  useEffect(() => { laden(wahl); }, [sorte, JSON.stringify(wahl)]);
  useEffect(() => { if (keinTb !== null) laden(wahl, true); }, [keinTb]);

  const setzeFach = (fach: string, wert: string) =>
    setWahl((w) => ({ ...w, [fach]: wert === '' ? null : wert }));

  if (!d) {
    return (
      <Dialog titel="An die Gemeinde senden" laeuft zu={zu} breite={820}>
        <Fehlerkasten lage={fehler} />
        {!fehler && <p className="klein leise">Wird zusammengestellt.</p>}
      </Dialog>
    );
  }

  const drin = (d.anhaenge ?? []).filter((a: any) => a.dateiId);
  const fotos = (d.anhaenge ?? []).filter((a: any) => a.fach.startsWith('FOTO:'));
  const weitere = (d.anhaenge ?? []).filter(
    (a: any) => a.fach.startsWith('DATEI:'));
  /* EIN FACH IST EIN FACH, EINE DATEI IST EINE DATEI.
     Die Auswahlfelder oben gehören den benannten Fächern („Lageplan",
     „Regelplan"): dort steht die FRAGE fest und die Datei wird gewählt. Ein
     Foto und ein weiterer Anhang sind das Umgekehrte - die Datei steht fest
     und gewählt wird, OB sie mitgeht. Beide tragen ihre Kennung im Fach
     hinter einem Doppelpunkt, und genau daran werden sie getrennt.

     HIER STAND `!a.fach.startsWith('FOTO:')`, und damit wären die weiteren
     Anhänge aus Punkt 21 als Auswahlfelder in der oberen Liste gelandet -
     zwanzig Zeilen „Tiefbau/01 Vorher" mit je einem Dropdown über alle
     Dateien des Auftrags. **Ein Filter, der eine Sorte beim Namen nennt,
     fängt die nächste nicht mit.** */
  const faecher = (d.anhaenge ?? []).filter(
    (a: any) => !a.fach.includes(':'));
  const kannSenden = !d.fehltPflicht.length && !d.zuGross && !d.portal
    && d.empfaenger.length > 0;
  /* BEI EINER FREIGABE STEHEN DIE FOTOS GANZ OBEN (.233): genau sie werden
     geprüft, und auf dem Tablet sollen sie ohne Rollen zu sehen sein. */
  const papiere = faecher.filter((a: any) => a.dateiId && a.bezugId);
  const fotoblock = (
        <>
          {freigabe && papiere.length > 0 && (
            <>
              <h4>Das geht an die Gemeinde</h4>
              <div className="dateigitter">
                {papiere.map((a: any) => (
                  <Dateikachel key={a.fach} datei={kacheldatei(a)} gewaehlt
                               oeffne={(x) => zeigeDatei(x, papiere.map(kacheldatei))} />
                ))}
              </div>
            </>
          )}
          <h4>Fotos der Fertigstellung</h4>
          {/* NUR DER ÖFFENTLICHE BEREICH. Wörtlich aus dem Bestand: „An die
              Behörde geht ausschließlich der öffentliche Bereich." */}
          {fotos.length === 0 ? (
            <p className="klein leise">
              Im Ordner „04 Fertigstellung öffentlicher Bereich" liegt noch
              kein Bild. An die Behörde geht ausschließlich dieser Ordner.
            </p>
          ) : (
            <Anlagengitter anlagen={fotos} setzeFach={setzeFach}
                           zeige={zeigeDatei} />
          )}
        </>
  );

  return (
    <Dialog titel={`${d.titel} an die Gemeinde`}
            untertitel={d.gemeinde ?? undefined}
            laeuft={laeuft} zu={zu} breite={820}>
      <Fehlerkasten lage={fehler} />

      {sorte === 'faz' && (
        <label className="hinweis" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!d.keinTbOeffentlich}
                 onChange={(e) => setKeinTb(e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            <b>Kein Tiefbau im öffentlichen Bereich.</b>{' '}
            <span className="klein leise">
              Vor Ort festgestellt: im öffentlichen Grund wurde nicht gegraben. Die FAZ geht dann ohne
              Fertigstellungsfotos hinaus, mit der Vorlage dafür. Die Kennzeichnung bleibt am Auftrag.
            </span>
          </span>
        </label>
      )}
      {sorte === 'faz' && freigabe && !d.keinTbOeffentlich && fotoblock}

      {/* WER SIE BEKOMMT, STEHT OBEN. Beim Pollerantrag ist das ein ANDERES
          Amt als bei der Aufgrabung - der Bestand nennt den stillen Rückfall
          darauf „den gefaehrlichsten Fehler". */}
      <div className={'hinweis ' + (d.empfaenger.length ? 'gut' : 'fehler')}>
        <b>Geht an:</b>{' '}
        {d.empfaenger.length ? d.empfaenger.join(', ')
          : sorte === 'poller'
            ? 'niemanden. Für Pollerfreigaben ist bei dieser Gemeinde keine '
              + 'eigene Adresse hinterlegt - und die Adresse der Aufgrabung '
              + 'gilt hier ausdrücklich nicht, das ist ein anderes Amt.'
            : 'niemanden. Es fehlt die Behördenadresse in den Stammdaten.'}
      </div>

      {d.portal && (
        <div className="hinweis warnung">
          <b>Diese Gemeinde nimmt das über ihr Portal an.</b> Hier geht nichts
          hinaus. Die Unterlagen liegen im Auftragsordner und gehören
          eingestellt unter {d.portal.url ?? '(keine Adresse hinterlegt)'}.
        </div>
      )}

      {!!d.fehltPflicht.length && (
        <div className="hinweis fehler">
          <b>Es fehlt: {d.fehltPflicht.join(', ')}.</b> Der Text der Mail
          kündigt die Anlagen an; bitte das Papier zuerst erzeugen.
        </div>
      )}
      {!!d.fehltWeich.length && (
        <div className="hinweis warnung">
          <b>Nicht gefunden: {d.fehltWeich.join(', ')}.</b> Die Sendung geht
          trotzdem hinaus - hier steht ein Mensch davor und entscheidet.
        </div>
      )}
      {d.zuGross && <div className="hinweis fehler">{d.zuGross}</div>}

      {/* ======================================================================
          DIE ANLAGEN STEHEN UNTEREINANDER, NICHT IN ZWEI SPALTEN.
          ======================================================================
          ORIGINALTEXT MARVIN (03.09.2026, Bildschirmfoto des Fensters)
            „die formatierung passt nicht, das musst du beheben und generell
             auch auch über anders prüfen denn hier jetzt bei den behörden zb
             ist vao und faz und poller auch falsch, damit der fehler dauerhaft
             weg ist."

          Auf seinem Bild stand links „Verkehrsrechtliche Anordnung, Pflicht"
          über drei Zeilen und rechts daneben ein Auswahlfeld, das über den
          Kasten hinausragte. Zwei Spalten mit einer festen Breite von 210
          Pixeln tragen eine Beschriftung von zwei Wörtern und keine von
          fünf - und der Dateiname daneben ist regelmässig sechzig Zeichen
          lang.

          UND ES IST DASSELBE FENSTER FÜR ALLE DREI SORTEN, deshalb ist es
          hier EINMAL geändert und nicht dreimal. Genau das meint „damit der
          fehler dauerhaft weg ist". */}
      <h4>Diese Dateien gehen mit</h4>
      <div className="anlagen">
        {/* WAS DAS SYSTEM SELBST GEFUNDEN HAT, IST GRÜN.
            ORIGINALTEXT MARVIN (08.09.2026)
              „bei der behördenpost die automatisch ausgewählten anhänge grün
               markieren."
            **GEMESSEN WIRD NICHT „STEHT EINE DATEI DA", SONDERN „HAT SIE
            JEMAND AUSGESUCHT".** Der Server liefert dafür seit dem
            03.09.2026 `gewaehlt` mit: es ist wahr, sobald ein Mensch dieses
            Fach angefasst hat - auch wenn er es abgewählt hat. Ein Fach mit
            Datei und OHNE diese Marke ist das, was die Regeln von selbst
            gefunden haben, und genau danach ist gefragt. Wer statt dessen
            `dateiId` grün färbte, färbte auch das, was der Mensch gerade
            selbst eingetragen hat - und dann sagt die Farbe nichts mehr. */}
        {faecher.map((a: any) => (
          <div key={a.fach}
               className={'anlage' + (a.dateiId && !a.gewaehlt ? ' selbst' : '')}>
            <div className="klein">
              <span className={'zustandspunkt '
                + (a.dateiId ? 'gut' : a.pflicht ? 'hart' : 'offen')} />
              {a.beschriftung}
              {a.pflicht && <span className="leise">, Pflicht</span>}
              {a.dateiId && !a.gewaehlt && (
                <span className="merker gruen" style={{ marginLeft: 6 }}>
                  selbst gefunden
                </span>
              )}
            </div>
            <select value={a.dateiId ?? ''}
                    aria-label={a.beschriftung}
                    onChange={(e) => setzeFach(a.fach, e.target.value)}>
              <option value="">(keine Datei)</option>
              {(d.auswahl ?? []).map((f: any) => (
                <option key={f.id} value={f.id}>{f.dateiname}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {sorte === 'faz' && !freigabe && !d.keinTbOeffentlich && fotoblock}

      {/* ======================================================================
          WEITERE ANHÄNGE, MIT VORSCHAUBILD.
          ======================================================================
          ORIGINALTEXT MARVIN (08.09.2026, Punkt 21)
            „Behördenpost: weitere Anhänge hinzufügen mit Vorschaubildern
             (Auswahl oder Dateiablage)"

          SEIN KLAMMERZUSATZ NENNT ZWEI WEGE, UND ES IST EINER. „Auswahl oder
          Dateiablage" heisst: entweder ich suche etwas aus, oder ich gehe in
          den Dateireiter und lade es dort hoch. Der zweite Weg endet
          wieder in diesem Fenster - was im Auftragsordner liegt, steht hier.
          Ein zweites Hochladefeld an dieser Stelle wäre ein FÜNFTER Weg
          hinein neben den vieren, die es gibt, und dieses Haus hat genau EIN
          Dateifeld (`teile/Abwurf.tsx`).

          DAS VORSCHAUBILD IST DER PUNKT UND KEINE ZIER. Zwanzig Namen wie
          „IMG_20240513_1015" untereinander sind keine Auswahl: welches Bild
          die aufgerissene Fahrbahn zeigt und welches den Schaltschrank, steht
          im Namen nicht. */}
      <h4>{sorte === 'faz' ? 'Weitere Fertigstellungsbilder' : 'Weitere Anhänge'}</h4>
      {weitere.length === 0 ? (
        <p className="klein leise">
          {sorte === 'faz'
            ? 'Weitere Fertigstellungsbilder liegen an diesem Auftrag nicht.'
            : 'An diesem Auftrag liegt nichts, was zusätzlich mitgehen könnte.'}
          {' '}Was hochgeladen wird, steht anschliessend hier.
        </p>
      ) : (
        <>
          <p className="klein leise">
            {sorte === 'faz'
              ? 'Nur Bilder aus den Fertigstellungsordnern. Ab Werk geht nichts davon mit; was angehakt ist, hängt an der Mail.'
              : 'Ab Werk geht nichts davon mit. Was hier angehakt ist, hängt an der Mail - der Ordner steht unter dem Bild.'}
          </p>
          <Anlagengitter anlagen={weitere} setzeFach={setzeFach}
                         zeige={zeigeDatei} mitOrdner />
        </>
      )}

      {/* DAS FENSTER GEHÖRT DER SENDUNG. Wer eine Anlage aufmacht, blättert
          durch die Anlagen DIESER Sendung und nicht durch den Auftrag. */}
      {dateifenster}

      {/* DIE VORLAGE KOMMT AUS DER KONFIGURATION. Fehlt sie, geht nichts
          hinaus, und der Satz sagt, wo sie hingehört - ein erfundener Text
          ginge an eine Behörde und klänge dort anders als die letzten
          dreihundert. */}
      {d.fehltVorlage && (
        <div className="hinweis fehler"><b>{d.fehltVorlage}</b></div>
      )}

      {/* BETREFF UND TEXT ÜBER DIE GANZE BREITE, die Beschriftung DARÜBER.
          Hier stand ein `feldpaar`: die Beschriftung links, das Feld rechts -
          richtig für „PLZ" und falsch für einen Brief. Auf Marvins Bild
          brach „Text der Mail" um und daneben stand ein Feld von zweihundert
          Pixeln. Ein Name ist drei Wörter lang und wächst nicht, ein Brief
          schon - dieselbe Lehre wie in der Konfiguration am 01.09.2026. */}
      <div className="feld breit">
        <label className="klein" htmlFor="post_betreff">Betreff</label>
        <input id="post_betreff" value={betreff}
               onChange={(e) => setBetreff(e.target.value)} />
      </div>
      <div className="feld breit">
        <label className="klein" htmlFor="post_text">Text der Mail</label>
        <textarea id="post_text" rows={12} value={text}
                  onChange={(e) => setText(e.target.value)} />
      </div>

      {testmodus && (
        <div className="hinweis warnung">
          Der Testmodus ist an. Die Mail geht an die Testadresse, nicht an die
          Gemeinde.
        </div>
      )}

      {/* DIE RÜCKFRAGE NENNT DIE DATEIEN. Genau wie im Bestand: „Diese N
          Datei(en) gehen raus" mit den Namen darunter. */}
      {frage && (
        <div className="hinweis">
          <b>{drin.length} {drin.length === 1 ? 'Datei geht' : 'Dateien gehen'}
          {' '}raus an {d.empfaenger.join(', ')}:</b>
          <ul style={{ margin: '6px 0 0 18px' }}>
            {drin.map((a: any) => <li key={a.fach}>{a.dateiname}</li>)}
          </ul>
        </div>
      )}

      {/* DIE KNÖPFE BLEIBEN IM BILD (.233): auf dem Tablet ist das Fenster
          länger als der Bildschirm, und wer die Fotos oben geprüft hat, soll
          nicht erst bis unter den Mailtext rollen, um zu entscheiden. */}
      <div className={'reihe' + (freigabe ? ' sendungsleiste' : '')} style={{ marginTop: 10 }}>
        {!frage ? (
          <button className="knopf haupt"
                  disabled={!kannSenden || laeuft || !!d.fehltVorlage}
                  onClick={() => setFrage(true)}
                  title={kannSenden ? undefined
                    : 'Es fehlt noch etwas - siehe oben.'}>
            {freigabe ? 'Freigeben und senden' : 'Senden'}
          </button>
        ) : (
          <button className="knopf haupt" disabled={laeuft}
                  onClick={async () => {
                    setLaeuft(true); setFehler(null);
                    try {
                      /* .546: deckt die Anordnung der Behörde den Termin, hält der Server an;
                         „Trotzdem senden" schickt die ausdrückliche Bestätigung mit. */
                      const trotz = trotzAnordnung ? { trotz_anordnung: true } : {};
                      const e = freigabe
                        ? await api.freigabeFreigeben(freigabe,
                            { sorte, wahl, betreff, text, ...trotz,
                              ...(sorte === 'faz' ? { kein_tb_oeffentlich: !!d.keinTbOeffentlich } : {}) })
                        : await api.behoerdenpostSenden(auftragId, {
                            sorte, wahl, betreff, text, ...trotz,
                            ...(sorte === 'faz' ? { kein_tb_oeffentlich: !!d.keinTbOeffentlich } : {}) });
                      fertig(e.klartext);
                    } catch (f) { const l = ausFehler(f); setFehler(l); setFrage(l.feld === 'trotz_anordnung'); if (l.feld === 'trotz_anordnung') setTrotzAnordnung(true); }
                    finally { setLaeuft(false); }
                  }}>
            {laeuft ? 'Einen Moment'
                    : trotzAnordnung ? 'Trotzdem senden (Anordnung reicht nicht)'
                    : freigabe ? 'Ja, freigeben und senden' : 'Ja, senden'}
          </button>
        )}
        {freigabe && abgelehnt && !ablehnen && (
          <button className="knopf gefahr" disabled={laeuft}
                  onClick={() => { setAblehnen(true); setFrage(false); }}>
            Ablehnen
          </button>
        )}
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>

      {ablehnen && (
        <div className="ablehnkasten">
          <label className="klein" htmlFor="ablehn_kommentar">
            <b>Kommentar an {partner ?? 'die Partnerfirma'}</b>
            {' '}(geht beim Absenden per Telegram direkt an den Partner)
          </label>
          <textarea id="ablehn_kommentar" rows={4} autoFocus value={kommentar}
                    placeholder="z. B. Foto 2 zeigt die Oberfläche nicht, bitte neu aufnehmen."
                    onChange={(e) => setKommentar(e.target.value)} />
          <div className="reihe" style={{ marginTop: 8 }}>
            <button className="knopf haupt" disabled={laeuft || !kommentar.trim()}
                    onClick={async () => {
                      setLaeuft(true); setFehler(null);
                      try {
                        const e = await api.freigabeAblehnen(freigabe, kommentar.trim(), await fragen('Den Kommentar per Telegram an die Firma pushen?\n\nOK = ja, pushen\nAbbrechen = nein, nicht pushen'));
                        abgelehnt(e.klartext);
                      } catch (f) { setFehler(ausFehler(f)); }
                      finally { setLaeuft(false); }
                    }}>
              {laeuft ? 'Einen Moment' : 'Ablehnen und an Partner senden'}
            </button>
            <button className="knopf" disabled={laeuft}
                    onClick={() => setAblehnen(false)}>Zurück</button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

/**
 * ABGELEHNT WIRD MIT GRUND.
 *
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „hintergrund ist, dass schlechte fotos raus gehen und wir die leute
 *    erstmal schulen müssen"
 *
 * Geschult wird mit dem SATZ und nicht mit dem Knopf. „Nicht freigegeben"
 * schickt niemanden an eine Stelle, an der er etwas tun kann; „Foto 3 zeigt
 * die Grube nicht, bitte neu" schon. Der Riegel liegt im CHECK der Tabelle
 * und im Server - hier steht er noch einmal, damit der Knopf nicht auf eine
 * rote Meldung führt.
 *
 * UND DER GRUND GEHT AN DEN, DER ES ÄNDERN MUSS: er landet in der
 * Auftragsinfo, sichtbar für die Partnerfirma.
 */
function Ablehnung({ x, zu, lehneAb, laeuft, fehler }: any) {
  const [grund, setGrund] = useState('');
  return (
    <Dialog titel={`${String(x.sorte).toUpperCase()} nicht freigeben`}
            laeuft={laeuft} zu={zu} breite={620}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise" style={{ marginTop: 0 }}>
        Die Sendung geht NICHT hinaus. Die Papiere bleiben, wo sie sind; nach
        dem besseren Foto wird neu gesendet.
      </p>
      <Text name="grund" titel="Was ist zu ändern?" pflicht mehrzeilig
            wert={grund} setze={setGrund} fehler={fehler}
            hinweis={`Geht beim Absenden per Telegram an ${x.partner_firma ?? 'die Tiefbaufirma'} und steht in der Auftragsinfo.`} />
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt" disabled={laeuft || !grund.trim()}
                onClick={() => lehneAb(grund.trim())}>
          {laeuft ? 'Einen Moment' : 'Nicht freigeben'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}

/**
 * EIN FENSTER, DAS ETWAS WEGNIMMT UND NACH DEM GRUND FRAGT.
 *
 * ORIGINALTEXT MARVIN (07.09.2026)
 *   „wieso kann ich die dateien in der behördenmappe nicht löschen?"
 *
 * ZWEIMAL DASSELBE FENSTER UND NICHT ZWEI. „Datei in den Papierkorb" und
 * „Papier zurückziehen" nehmen Verschiedenes weg und stellen dieselbe Frage;
 * 2 Abschriften liefen beim ersten Nachtrag auseinander, und dann verlangt
 * das eine einen Grund und das andere nicht.
 *
 * DER GRUND IST NICHT ÜBERALL PFLICHT, und der Unterschied hat einen Grund:
 * eine Datei liegt danach im Papierkorb und lässt sich zurückholen, ein
 * zurückgezogenes Papier nimmt eine FRIST gegenüber einer Behörde mit. Dort
 * liegt der Riegel doppelt - hier im Fenster und im CHECK der Datenbank.
 */
function MitGrund({
  titel, untertitel, satz, knopf, pflicht, zu, tuEs, laeuft, fehler,
}: {
  titel: string; untertitel?: string; satz: string; knopf: string;
  pflicht?: boolean; laeuft: boolean; fehler: Fehlerlage | null;
  zu: () => void; tuEs: (grund: string) => Promise<void>;
}) {
  const [grund, setGrund] = useState('');
  return (
    <Dialog titel={titel} untertitel={untertitel} laeuft={laeuft} zu={zu}
            breite={620}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise" style={{ marginTop: 0 }}>{satz}</p>
      <Text name="grund" titel="Grund" pflicht={pflicht} mehrzeilig
            wert={grund} setze={setGrund} fehler={fehler}
            hinweis="Er steht danach im Protokoll und in der Auftragsinfo." />
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt warnung"
                disabled={laeuft || (!!pflicht && !grund.trim())}
                onClick={() => tuEs(grund.trim())}>
          {laeuft ? 'Einen Moment' : knopf}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}

function Eingang({ zu, erfasse, laeuft, fehler }: any) {
  const [art, setArt] = useState('VAO');
  const [f, setF] = useState<Record<string, string>>({});
  const [bestaetigt, setBestaetigt] = useState(false);
  const setze = (k: string, v: string) => setF({ ...f, [k]: v });

  return (
    <Dialog titel="Eingegangenes Behördenpapier erfassen" laeuft={laeuft} zu={zu}
            breite={680}>
      <Fehlerkasten lage={fehler} />
      <Haken
        name="von_der_behoerde"
        titel="Dieses Papier kam wirklich von der Behörde"
        wert={bestaetigt} setze={setBestaetigt}
        hinweis="Unser eigener Antrag heißt fast genauso und enthält dieselben
                 Wörter. Nur der Bescheid trägt eine Frist. Früher sind aus
                 dieser Verwechslung 158 falsche Haken entstanden."
      />
      <Auswahl name="art" titel="Art" pflicht wert={art} setze={setArt}
               leerText="" breite="100%" fehler={fehler} optionen={EINGANG} />
      <div className="feldreihe">
        <Text name="aktenzeichen" titel="Aktenzeichen" wert={f.aktenzeichen}
              setze={(v) => setze('aktenzeichen', v)} fehler={fehler} breite={220} />
        <Text name="eingegangen_am" titel="Eingegangen am" art="date"
              wert={f.eingegangen_am} setze={(v) => setze('eingegangen_am', v)}
              fehler={fehler} hinweis="leer = heute" />
      </div>
      <div className="feldreihe">
        <Text name="gueltig_von" titel="Gültig von" art="date" wert={f.gueltig_von}
              setze={(v) => setze('gueltig_von', v)} fehler={fehler} />
        <Text name="gueltig_bis" titel="Gültig bis" art="date" wert={f.gueltig_bis}
              setze={(v) => setze('gueltig_bis', v)} fehler={fehler}
              hinweis="Das ist die Frist, auf die später gewarnt wird." />
        <Text name="gebuehr_betrag" titel="Gebühr in Euro" wert={f.gebuehr_betrag}
              setze={(v) => setze('gebuehr_betrag', v)} fehler={fehler} breite={160}
              hinweis="nur die durchgereichte Behördengebühr" />
      </div>
      <Text name="dateiname" titel="Dateiname (falls schon hochgeladen)"
            wert={f.dateiname} setze={(v) => setze('dateiname', v)}
            breite="100%" fehler={fehler} />
      <Text name="bemerkung_intern" titel="Interne Bemerkung" mehrzeilig wert={f.bemerkung_intern}
            setze={(v) => setze('bemerkung_intern', v)} fehler={fehler} />
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt" disabled={laeuft || !bestaetigt}
                onClick={() => erfasse({ art, von_der_behoerde: true, ...f })}>
          {laeuft ? 'Einen Moment' : 'Erfassen'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


function Versenden({ x, gemeinde, empfaenger, testmodus, zu, sende, portal,
                     laeuft, fehler }: any) {
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [az, setAz] = useState('');
  const perPortal = (gemeinde?.portal_wege ?? []).includes(x.art);
  /* WER DAS BEKOMMT, RECHNET DER SERVER. Hier stand `behoerde_mail_an`, und
     damit war der Knopf bei einer Gemeinde, die nur den Polleradressaten
     gepflegt hat, grau - obwohl der Versandweg die Adresse kennt. Der
     Rückfall bleibt für eine ältere Antwort stehen, die `empfaenger` noch
     nicht mitschickt. */
  const an = empfaenger?.[x.art] ?? gemeinde?.behoerde_mail_an ?? [];

  return (
    <Dialog titel={`${dokumentArt(x.art)} versenden`} laeuft={laeuft} zu={zu} breite={680}>
      <Fehlerkasten lage={fehler} />
      {perPortal ? (
        <>
          <div className="hinweis warnung">
            {gemeinde.gemeinde ?? 'Diese Gemeinde'} nimmt {dokumentArt(x.art)} nur über das
            Portal an. Das Papier liegt im Auftragsordner und gehört dort
            eingestellt:{' '}
            {gemeinde.portal_url
              ? <a href={gemeinde.portal_url} target="_blank" rel="noreferrer">
                  {gemeinde.portal_url}
                </a>
              : <b>keine Adresse hinterlegt</b>}
          </div>
          <Text name="aktenzeichen" titel="Aktenzeichen aus dem Portal"
                wert={az} setze={setAz} breite={280} fehler={fehler} />
          <div className="reihe" style={{ marginTop: 10 }}>
            <button className="knopf haupt" disabled={laeuft}
                    onClick={() => portal(az)}>
              Als eingestellt vermerken
            </button>
            <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
          </div>
        </>
      ) : (
        <>
          <p className="klein">
            Geht an: <b>{an.length ? an.join(', ') : 'niemanden, es fehlt die Adresse'}</b>
          </p>
          {testmodus && (
            <div className="hinweis warnung">
              Testmodus: die Mail geht an die Testadresse. Die Gemeinde bekommt
              nichts. Der wirklich vorgesehene Empfänger steht im Text der Mail
              und im Versandprotokoll.
            </div>
          )}
          <Text name="betreff" titel="Betreff" wert={betreff} setze={setBetreff}
                breite="100%" fehler={fehler}
                hinweis="leer = Art, Anschrift und KLS-Nummer" />
          <Text name="text" titel="Text" mehrzeilig wert={text} setze={setText}
                fehler={fehler}
                hinweis="leer = das Standardanschreiben" />
          <p className="klein leise">Das Papier hängt als PDF an.</p>
          <div className="reihe" style={{ marginTop: 10 }}>
            <button className="knopf haupt" disabled={laeuft || !an.length}
                    onClick={() => sende({ betreff: betreff || undefined,
                                           text: text || undefined,
                                           stand: x.updated_at })}>
              {laeuft ? 'Einen Moment' : 'Versenden'}
            </button>
            <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
          </div>
        </>
      )}
    </Dialog>
  );
}


/** Aktenzeichen, Frist und Gebuehr nachtragen. */
function Aendern({ x, speichere, laeuft, fehler }: any) {
  const [offen, setOffen] = useState(false);
  const [f, setF] = useState<Record<string, any>>({});
  const vonBehoerde = x.quelle === 'behoerde';
  const setze = (k: string, v: any) => setF({ ...f, [k]: v });

  useEffect(() => {
    if (offen) {
      setF({
        /* WAS NICHT GEFRAGT WIRD, WIRD AUCH NICHT GESCHICKT. Ein Feld, das
           der Bildschirm nicht zeigt und der Rumpf trotzdem trägt, ist eine
           Änderung, die niemand vorgenommen hat. */
        ...(vonBehoerde ? { aktenzeichen: x.aktenzeichen ?? '' } : {}),
        gueltig_von: x.gueltig_von ? String(x.gueltig_von).slice(0, 10) : '',
        gueltig_bis: x.gueltig_bis ? String(x.gueltig_bis).slice(0, 10) : '',
        gebuehr_betrag: x.gebuehr_betrag ?? '',
        frist_geprueft: x.frist_geprueft,
        bemerkung_intern: x.bemerkung_intern ?? '',
      });
    }
  }, [offen]);

  if (!offen) {
    return <button className="knopf klein" onClick={() => setOffen(true)}>ändern</button>;
  }
  return (
    <Dialog titel={`${dokumentArt(x.art)} ändern`} laeuft={laeuft} zu={() => setOffen(false)}
            breite={620}>
      <Fehlerkasten lage={fehler} />
      {/* AN EINEM EIGENEN PAPIER STEHT NUR DIE BEMERKUNG.
          ORIGINALTEXT MARVIN (07.09.2026)
            „wenn ich etwas ändern will dann brauche ich diese meldung mit
             aktenzeichen und grund nicht."
          Hier stand ein Feld „Aktenzeichen" und daneben der Satz, dass ein
          eigenes Papier weder Frist noch Gebühr trägt. **Ein Formular, das
          ein Feld zeigt und daneben erklärt, dass es hier nichts bedeutet,
          ist ein Feld zu viel.** Das Aktenzeichen vergibt die Behörde, und
          ihre Antwort ist ein eigener Eintrag in der Mappe. Der Riegel steht
          im Server; hier fällt nur die Frage weg. */}
      {vonBehoerde && (
        <Text name="aktenzeichen" titel="Aktenzeichen" wert={f.aktenzeichen}
              setze={(v) => setze('aktenzeichen', v)} breite={280}
              fehler={fehler} />
      )}
      {vonBehoerde && (
        <>
          <div className="feldreihe">
            <Text name="gueltig_von" titel="Gültig von" art="date"
                  wert={f.gueltig_von} setze={(v) => setze('gueltig_von', v)}
                  fehler={fehler} />
            <Text name="gueltig_bis" titel="Gültig bis" art="date"
                  wert={f.gueltig_bis} setze={(v) => setze('gueltig_bis', v)}
                  fehler={fehler} />
            <Text name="gebuehr_betrag" titel="Gebühr in Euro"
                  wert={f.gebuehr_betrag} setze={(v) => setze('gebuehr_betrag', v)}
                  fehler={fehler} breite={150} />
          </div>
          <Haken name="frist_geprueft" titel="Frist geprüft"
                 wert={!!f.frist_geprueft}
                 setze={(v) => setze('frist_geprueft', v)} />
        </>
      )}
      <Text name="bemerkung_intern" titel="Interne Bemerkung" mehrzeilig wert={f.bemerkung_intern}
            setze={(v) => setze('bemerkung_intern', v)} fehler={fehler} />
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt" disabled={laeuft}
                onClick={async () => {
                  await speichere({ ...f, stand: x.updated_at });
                  setOffen(false);
                }}>
          Speichern
        </button>
        <button className="knopf" onClick={() => setOffen(false)} disabled={laeuft}>
          Abbrechen
        </button>
      </div>
    </Dialog>
  );
}


/**
 * Welche Datei zu diesem Behördenpapier gehört.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „Ich muss die Möglichkeit haben, wenn hier Dateien vorhanden sind in der
 *    Behördenmappe, dass ich die auch anklicken kann."
 *
 * ES WIRD NICHTS HOCHGELADEN, SONDERN VERKNÜPFT. Die Datei liegt im Reiter
 * „Dateien" desselben Auftrags; ein zweiter Hochladeweg brächte eigene Ordner-
 * und Rechteregeln mit und endete in zwei Kopien, von denen eine die falsche
 * ist. Liegt noch gar nichts da, sagt dieser Dialog das - und WOHIN man dann
 * geht. Eine leere Liste ohne Satz daneben liest sich wie ein Fehler.
 *
 * DASS EINE DATEI SCHON WOANDERS HÄNGT, STEHT DABEI. Sie lässt sich trotzdem
 * wählen (dasselbe PDF kann zu zwei Papieren gehören), aber dann weiss man es
 * vorher.
 */
function DateiZuordnen({
  x, auftragId, laeuft, fehler, zu, setze,
}: {
  x: any; auftragId: string; laeuft: boolean; fehler: Fehlerlage | null;
  zu: () => void; setze: (bezugId: string | null) => Promise<void>;
}) {
  const [liste, setListe] = useState<any[] | null>(null);
  const [ladefehler, setLadefehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    let weg = false;
    (async () => {
      try {
        const l = await api.dateienZurAuswahl(auftragId);
        if (!weg) setListe(l);
      } catch (f) { if (!weg) setLadefehler(ausFehler(f)); }
    })();
    return () => { weg = true; };
  }, [auftragId]);

  return (
    <Dialog titel={`Datei für ${dokumentArt(x.art)}`} untertitel={x.aktenzeichen ?? undefined}
            breite={640} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler ?? ladefehler} />

      {liste === null && <p className="klein leise">Die Ablage wird gelesen.</p>}

      {liste !== null && liste.length === 0 && (
        <p className="klein leise">
          Zu diesem Auftrag liegt noch keine Datei in der Ablage. Laden Sie sie
          im Reiter „Dateien" hoch; danach steht sie hier zur Auswahl.
        </p>
      )}

      {liste !== null && liste.length > 0 && (
        <table className="liste">
          <thead>
            <tr>
              <th>Datei</th>
              <th style={{ width: 150 }}>Ordner</th>
              <th style={{ width: 110 }}>Hochgeladen</th>
              <th style={{ width: 110 }} />
            </tr>
          </thead>
          <tbody>
            {liste.map((b: any) => (
              <tr key={b.id} style={{ cursor: 'default' }}>
                <td>
                  {b.dateiname}
                  {b.dokument_id && b.dokument_id !== x.id && (
                    <div className="klein leise">
                      hängt schon an einem anderen Papier
                    </div>
                  )}
                </td>
                <td className="klein leise">{b.ordner}</td>
                <td className="klein leise">{datum(b.hochgeladen_am)}</td>
                <td>
                  {b.dokument_id === x.id
                    ? <span className="klein leise">hängt hier</span>
                    : <button className="knopf klein"
                              onClick={() => setze(b.id)}>wählen</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="knopfreihe" style={{ marginTop: 14 }}>
        {x.datei && (
          /* DAS LÖSEN STEHT LINKS UND SIEHT NICHT AUS WIE DAS WÄHLEN. Es nimmt
             etwas weg, und ein Knopf, der wegnimmt, trägt nicht dieselbe Farbe
             wie einer, der etwas hinzufügt. */
          <button className="knopf warnung" onClick={() => setze(null)}>
            Verknüpfung lösen
          </button>
        )}
        <button className="knopf" onClick={zu}>Schließen</button>
      </div>
    </Dialog>
  );
}


/* ============================================================================
 * ANORDNUNG OHNE ZEITRAUM PRÜFEN (.245)
 * ============================================================================
 * Je Datei eine Zeile: öffnen, „Zeitraum erfassen" (von, bis) oder
 * „Abgelaufen". Danach entscheidet der Server neu: deckt sie den
 * Tiefbautermin, passiert nichts; sonst entstehen AAZ und VAO.
 * ==========================================================================*/
function AnordnungPruefen({ liste, auftragId, darf, fertig }: {
  liste: any[]; auftragId: string; darf: boolean; fertig: (klartext: string) => void;
}) {
  const [offen, setOffen] = useState<string | null>(null);
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const schluessel = (x: any) => x.dokument_id ?? x.bezug_id;

  async function schicken(x: any, abgelaufen: boolean) {
    if (abgelaufen && !await fragen(`„${x.dateiname}" als abgelaufen markieren? Dann wird, wenn ein Tiefbautermin steht, neu beantragt.`)) return;
    setLaeuft(true); setFehler(null);
    try {
      const r = await api.anordnungPruefen(auftragId, {
        dokument_id: x.dokument_id, bezug_id: x.dokument_id ? null : x.bezug_id,
        ...(abgelaufen ? { abgelaufen: true } : { gueltig_von: von || undefined, gueltig_bis: bis }),
      });
      setOffen(null); setVon(''); setBis('');
      fertig(r.klartext);
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  return (
    <div className="hinweis warnung">
      <b>Anordnung ohne Zeitraum: bitte prüfen</b>
      <div className="klein" style={{ marginTop: 4 }}>
        Das Datum entscheidet. Solange es fehlt, weiß das System nicht, ob der
        Tiefbautermin gedeckt ist, und stellt keinen neuen Antrag.
      </div>
      <Fehlerkasten lage={fehler} />
      {liste.map((x) => (
        <div key={schluessel(x)} style={{ marginTop: 8 }}>
          <div className="reihe" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {x.bezug_id
              ? <a href={api.dateiAdresse(x.bezug_id, x.dateiname)} target="_blank" rel="noreferrer">{x.dateiname}</a>
              : <span>{x.dateiname}</span>}
            {darf && offen !== schluessel(x) && (
              <>
                <button className="knopf klein" disabled={laeuft}
                        onClick={() => { setOffen(schluessel(x)); setVon(''); setBis(''); }}>
                  Zeitraum erfassen
                </button>
                <button className="knopf klein" disabled={laeuft}
                        onClick={() => schicken(x, true)}>
                  Abgelaufen
                </button>
              </>
            )}
          </div>
          {offen === schluessel(x) && (
            <div className="reihe" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'flex-end', marginTop: 6 }}>
              <label className="klein">von<br />
                <input type="date" value={von} onChange={(e) => setVon(e.target.value)} />
              </label>
              <label className="klein">bis<br />
                <input type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
              </label>
              <button className="knopf klein haupt" disabled={laeuft || !bis}
                      onClick={() => schicken(x, false)}>Speichern</button>
              <button className="knopf klein" disabled={laeuft} onClick={() => setOffen(null)}>Abbrechen</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
